import {
    createAsyncThunk,
    createSlice,
    isAnyOf,
    PayloadAction,
} from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import { LanguageService } from '../../services';
import QuestionnaireActions from '../../participant/actions/QuestionnaireActions';
import OutputReportActions from '../../actions/OutputReportActions';
import InfoActions from '../../actions/InfoActions';
import defaultLocales from '../../config/defaultLocales.json';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';
import UserService from '../services/UserService';

const NAME = 'Language';

export type LanguageState = {
    locale: string | undefined;
    prevLocale: string | undefined;
    allowedLocales: string[];
    navDisabled: boolean;
    loading: boolean;
    canChangeLocale: boolean;
};

const initialState: LanguageState = {
    locale: undefined,
    prevLocale: undefined,
    allowedLocales: [],
    navDisabled: false,
    loading: false,
    canChangeLocale: true,
};

const changeLocale = createAsyncThunk(
    `${NAME}/changeLocale`,
    LanguageService.setLocale,
);

// The backend rejects a locale outside the user's allowed languages; the error tells the user
// instead of the change silently not happening
const setUserLocale = createNotifiedThunk<string, string>(
    `${NAME}/setUserLocale`,
    async (locale: string, { getState }) => {
        const localLocale: string = LanguageService.setLocale(locale);
        const isLoggedIn = (getState() as AppState).auth.user.isLoggedIn;

        if (!isLoggedIn) {
            return localLocale;
        }

        const response = await UserService.setLocale(localLocale);
        return response?.data?.locale ?? localLocale;
    },
    { error: 'fe.participant.language.change-failed' },
);

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {
        changePrevLocale: (
            state: LanguageState,
            action: PayloadAction<string>,
        ) => {
            state.prevLocale = action.payload;
        },
        changeAllowedLocales: (
            state: LanguageState,
            action: PayloadAction<string[]>,
        ) => {
            state.allowedLocales = action.payload;
        },
        setLoading: (state: LanguageState, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setCanChangeLocale: (
            state: LanguageState,
            action: PayloadAction<boolean>,
        ) => {
            state.canChangeLocale = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            changeLocale.fulfilled,
            (state: LanguageState, action: PayloadAction<string>) => {
                state.locale = action.payload;
            },
        );

        builder.addCase(
            setUserLocale.fulfilled,
            (state: LanguageState, action: PayloadAction<string>) => {
                state.locale = action.payload;
            },
        );

        builder.addCase(LOGOUT, (state: LanguageState) => {
            state.allowedLocales = [];
            state.prevLocale = undefined;
            state.navDisabled = false;
            state.loading = false;
            state.canChangeLocale = true;
        });

        builder.addCase(InfoActions.getInfo.rejected, (state) => {
            if (!state.locale) {
                state.locale = LanguageService.getCurrentLocale(defaultLocales);
            }
        });

        builder.addMatcher(
            isAnyOf(
                QuestionnaireActions.setLoading,
                OutputReportActions.setDetailLoading,
            ),
            (
                state: LanguageState,
                action: { type: string; payload: boolean },
            ) => {
                state.navDisabled = action.payload;
            },
        );
        builder.addMatcher(
            isAnyOf(
                changeLocale.fulfilled,
                changeLocale.rejected,
                setUserLocale.fulfilled,
                setUserLocale.rejected,
            ),
            (state: LanguageState) => {
                state.loading = false;
            },
        );
    },
});

const state = (state: AppState) => state.common.language;

const LanguageSlice = {
    actions: { ...slice.actions, changeLocale, setUserLocale },
    reducer: slice.reducer,
    state: {
        getAll: state,
        getLocale: createSelector(
            state,
            (state: LanguageState) => state.locale,
        ),
        getPrevLocale: createSelector(
            state,
            (state: LanguageState) => state.prevLocale,
        ),
        getAllowedLocales: createSelector(
            state,
            (state: LanguageState) => state.allowedLocales,
        ),
        getNavDisabled: createSelector(
            state,
            (state: LanguageState) => state.navDisabled,
        ),
        getCanChangeLocale: createSelector(
            state,
            (state: LanguageState) => state.canChangeLocale,
        ),
    },
};

export default LanguageSlice;
